import * as vp from 'vopay';
import {
  Box,
  Tooltip,
  Pagination,
  Badge,
  CardTabs,
  formattedCurrency,
  capitalizeFirstLetter,
  GetColorStatus,
  usePagination,
  useUpdateQueryParams,
  MaterialSymbol,
} from 'vopay';
import { FunctionComponent, useState, useEffect, JSX } from 'react';
import { format, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useHostNavigate } from '../HostNavigationContext';
import useFileDownload from '#shared/utils/useFileDownload';
import useFetchELinxHistory from '../hooks/useFetchELinxHistory';
import ElinxHistorySearchForm from './ElinxHistorySearchForm';
import ElinxHistoryExport from './ElinxHistoryExport';
import ActivityType, { ActivityTabType } from './ActivityType';
import CopyButton from './CopyButton';
import type { ActivitySubTableProps } from './TransactionHistoryTable';
import styles from './ElinxHistoryTable.module.scss';

import { Button, Skeleton, Table, TableFiltersContainer, TransactionStatus } from '#shared/components/UI';
import toDayTimestamp from './toDayTimestamp';

type TabContents = {
  account: JSX.Element;
  payment: JSX.Element;
};

type QueryParams = {
  status?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  activityType?: ActivityTabType;
  eLinxType?: keyof TabContents;
};

type Filters = {
  status: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  timeZone: string;
};

type TransactionParams = Filters & {
  hasInvalidEndDate: boolean;
  offset?: number;
};

// The portal's Customer Activity → eLinx History table, adapted for the
// contact drawer/portal pages: filters live in memory (mirrored to the URL
// when persistFiltersInUrl) and the eLinx-details link-outs go through the
// host's onNavigate (plain text when the host can't navigate).
const ElinxHistoryTable: FunctionComponent<ActivitySubTableProps> = ({
  clientAccountId,
  clientAccountsEnabled,
  activityTypeOptions,
  currentType,
  handleCurrentType,
  persistFiltersInUrl,
  timezone,
}) => {
  const {
    downloadPdfTable,
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();

  const accountIDType = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const hostNavigate = useHostNavigate();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ElinxHistory' });
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const { fetching, fetchELinxHistory, totalRecords } = useFetchELinxHistory();
  const { fetchELinxHistory: fetchELinxHistoryExport } = useFetchELinxHistory();
  const [filteredList, setFilteredList] = useState<vp.ELinxHistoryItem[]>([]);
  const [exportList, setExportList] = useState<vp.ELinxHistoryItem[]>([]);

  const [activeTab, setActiveTab] = useState<keyof TabContents>(() => {
    const fromUrl = persistFiltersInUrl ? new URLSearchParams(window.location.search).get('eLinxType') : null;

    return fromUrl === 'payment' ? 'payment' : 'account';
  });

  const [columnSwap, setColumnSwap] = useState<'name' | 'email'>('name');
  const [idSwap, setIdSwap] = useState<'transaction' | 'schedule'>('transaction');

  const elinxRequestType: vp.ELinxRequestType[] =
    activeTab === 'account' ? ['beneficiary'] : ['scheduled', 'transaction', 'erp'];

  const defaultTimeZone = timezone || 'UTC';

  const initialFilters: Filters = {
    status: '',
    searchTerm: '',
    startDate: formatInTimeZone(subDays(new Date(), 7), defaultTimeZone, 'yyyy-MM-dd'),
    endDate: formatInTimeZone(new Date(), defaultTimeZone, 'yyyy-MM-dd'),
    timeZone: defaultTimeZone,
  };

  // Portal mode seeds the filters from the page URL (deep links, reload/back
  // restoration); the drawer starts from the in-memory defaults.
  const [filters, setFilters] = useState<Filters>(() => {
    if (!persistFiltersInUrl) return initialFilters;

    const urlParams = new URLSearchParams(window.location.search);
    const timeZone = urlParams.get('timeZone') || defaultTimeZone;

    return {
      status: urlParams.get('status') || '',
      searchTerm: urlParams.get('searchTerm') || '',
      startDate: urlParams.get('startDate') || formatInTimeZone(subDays(new Date(), 7), timeZone, 'yyyy-MM-dd'),
      endDate: urlParams.get('endDate') || formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'),
      timeZone,
    };
  });

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const filtersApplied = Boolean(
    filters.status ||
      filters.searchTerm ||
      filters.startDate !== initialFilters.startDate ||
      filters.endDate !== initialFilters.endDate ||
      filters.timeZone !== initialFilters.timeZone,
  );

  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();

  const handleTabChange = (value: string) => {
    if (value === 'account' || value === 'payment') {
      setActiveTab(value);
    }
  };

  // pagination
  const PER_PAGE = 20;

  const { pageCount, pageIndex, setPageIndex } = usePagination(totalRecords, {
    pageSize: PER_PAGE,
  });

  const fetchElinxHistory = async ({
    startDate,
    endDate,
    status,
    hasInvalidEndDate,
    searchTerm,
    timeZone,
    offset = 0,
  }: TransactionParams) => {
    const elinxHistoryRequest = {
      StartDateTime: startDate,
      EndDateTime: endDate,
      Timezone: timeZone,
      ELinxStatus: status,
      ELinxRequestType: elinxRequestType,
      SearchText: searchTerm,
      Limit: PER_PAGE,
      Offset: offset,
      [accountIDType]: clientAccountId,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);

      return;
    }

    try {
      const res = await fetchELinxHistory(elinxHistoryRequest);

      const ELinxRequests = Object.values(res.ELinxRequests || {});
      setFilteredList(ELinxRequests);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('ErrorMessages.fetchingError', { defaultValue: 'An error occurred while fetching eLinx history' });

      alertDialog.error(t('ErrorMessages.error', { defaultValue: 'Error' }), msg);
    }
  };

  useEffect(() => {
    setHasInvalidEndDate(filters.startDate > filters.endDate);
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    if (persistFiltersInUrl) {
      updateQueryParams({
        status: filters.status,
        searchTerm: filters.searchTerm,
        startDate: filters.startDate,
        endDate: filters.endDate,
        timeZone: filters.timeZone,
        activityType: 'eLinxHistory',
        eLinxType: activeTab,
      });
    }

    const offset = setPageIndex(0);

    fetchElinxHistory({
      ...filters,
      hasInvalidEndDate: false,
      offset,
    });
  }, [activeTab]);

  const handleExport = async (exportType: 'pdf' | 'csv' | 'xls' | 'doc' | 'text' | 'json' | 'xml' | 'print') => {
    const elinxHistoryRequest = {
      StartDateTime: filters.startDate,
      EndDateTime: filters.endDate,
      Timezone: filters.timeZone,
      ELinxStatus: filters.status,
      ELinxRequestType: elinxRequestType,
      SearchText: filters.searchTerm,
      [accountIDType]: clientAccountId,
    };

    blockingLoader.show();

    try {
      const exportElinxData = await fetchELinxHistoryExport(elinxHistoryRequest);
      setExportList(Object.values(exportElinxData.ELinxRequests || {}));

      switch (exportType) {
        case 'pdf':
          downloadPdfTable('export-elinx-history-table', t('title', { defaultValue: 'eLinx History' }));
          break;
        case 'csv':
          downloadCsvFile('export-elinx-history-table', t('title', { defaultValue: 'eLinx History' }));
          break;
        case 'xls':
          downloadXls('export-elinx-history-table', t('title', { defaultValue: 'eLinx History' }));
          break;
        case 'doc':
          downloadDoc('export-elinx-history-table', t('title', { defaultValue: 'eLinx History' }));
          break;
        case 'text':
          downloadTxt('export-elinx-history-table', t('title', { defaultValue: 'eLinx History' }));
          break;
        case 'json':
          downloadJson(
            Object.values(exportElinxData.ELinxRequests || {}),
            t('title', { defaultValue: 'eLinx History' }),
          );

          break;
        case 'xml':
          downloadXml('export-elinx-history-table', t('title', { defaultValue: 'eLinx History' }));
          break;
        case 'print':
          printFile('export-elinx-history-table');
          break;
        default:
          break;
      }
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('ErrorMessages.fetchingError', { defaultValue: 'An error occurred while fetching eLinx history' });

      alertDialog.error(t('ErrorMessages.error', { defaultValue: 'Error' }), msg);
    } finally {
      blockingLoader.hide();
    }
  };

  const tableProps: vp.TableProps<vp.ELinxHistoryItem> = {
    id: 'elinx-history-table',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'creationDate',
        header: t('Columns.creationDate', { defaultValue: 'Creation Date' }),
        align: 'left',
        field: 'RequestDate',
      },
      {
        id: 'eLinxID',
        header: t('Columns.elinxId', { defaultValue: 'eLinx ID' }),
        align: 'left',
        template: ({ data }) => (
          <Box gap="sm" ai="center">
            {/* Link-out via the host's onNavigate; plain text when the host can't navigate. */}
            {hostNavigate ? (
              <button
                type="button"
                className={styles.linkOut}
                onClick={() => hostNavigate(`/elinx-history/details/${data.ELinxRequestID}`, { eLinx: data })}
              >
                {data.ELinxRequestID.toString()}
              </button>
            ) : (
              <span>{data.ELinxRequestID.toString()}</span>
            )}

            <CopyButton content={data.ELinxRequestID.toString()} color="rgba(45, 45, 45, 0.6)" />
          </Box>
        ),
      },
      {
        id: 'eLinxType',
        header: t('Columns.elinxType', { defaultValue: 'eLinx Type' }),
        align: 'left',
        visible: activeTab === 'payment',
        template: ({ data }) =>
          data.PaymentType === 'withdraw'
            ? 'ePay'
            : data.PaymentType === 'fund'
              ? 'eFlex'
              : t('notApplicable', { defaultValue: 'N/A' }),
      },
      {
        id: 'requestStatus',
        header: t('Columns.requestStatus', { defaultValue: 'Request Status' }),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <Box flx="row" ai="center">
              <TransactionStatus status={GetColorStatus(data.Status)} label={capitalizeFirstLetter(data.Status)} />
            </Box>

            {activeTab === 'payment' && data.Status === 'completed' && (
              <Box ps="sm">
                <Tooltip
                  type="hover"
                  information={t('TooltipMessages.requestCompleted', {
                    defaultValue:
                      "The request has been completed. For more information about the transaction click on 'Details'.",
                  })}
                  position="top"
                  hasArrow
                  icon="info"
                />
              </Box>
            )}
          </Box>
        ),
      },
      {
        id: 'recipientName',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.recipientName', { defaultValue: 'Recipient Name' })}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'name',
        field: 'ReceiverName',
        onHeaderClick: () => {
          setColumnSwap('email');
        },
      },
      {
        id: 'recipientEmail',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.recipientEmail', { defaultValue: 'Recipient Email' })}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'email',
        field: 'ReceiverEmailAddress',
        onHeaderClick: () => {
          setColumnSwap('name');
        },
      },
      {
        id: 'transactionID',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.transactionId', { defaultValue: 'Transaction ID' })}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: activeTab === 'payment' && idSwap === 'transaction',
        template: ({ data }) => data.TransactionID?.toString() || t('notApplicable', { defaultValue: 'N/A' }),
        onHeaderClick: () => {
          setIdSwap('schedule');
        },
      },
      {
        id: 'scheduleID',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.scheduleId', { defaultValue: 'Schedule ID' })}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: activeTab === 'payment' && idSwap === 'schedule',
        template: ({ data }) => data.ScheduledPaymentsID?.toString() || t('notApplicable', { defaultValue: 'N/A' }),
        onHeaderClick: () => {
          setIdSwap('transaction');
        },
      },
      {
        id: 'account',
        header: t('Columns.account', { defaultValue: 'Account' }),
        align: 'left',
        template: ({ data }) =>
          data.Status === 'completed' ? (
            <>
              {data.AccountNumber && <span>{vp.getMaskedNumber(data.AccountNumber)}</span>}
              {data.CreditCardNumber && <span>{vp.getMaskedNumber(String(data.CreditCardNumber))}</span>}
            </>
          ) : (
            t('notApplicable', { defaultValue: 'N/A' })
          ),
      },
      {
        id: 'accountType',
        header: t('Columns.accountType', { defaultValue: 'Account Type' }),
        align: 'left',
        visible: activeTab === 'account',
        template: ({ data }) =>
          data.Status === 'completed' ? (
            <>
              {data.AccountNumber && t('bankAccount', { defaultValue: 'Bank Account' })}
              {data.CreditCardNumber && t('creditCard', { defaultValue: 'Credit Card' })}
            </>
          ) : (
            t('notApplicable', { defaultValue: 'N/A' })
          ),
      },
      {
        id: 'amount',
        header: t('Columns.amount', { defaultValue: 'Amount' }),
        align: 'left',
        visible: activeTab === 'payment',
        template: ({ data }) => `$${formattedCurrency(String(data.Amount))}`,
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {data.Status === 'pending' && (
              <Button
                id={`copy-url-${data.TransactionID}`}
                size="small"
                onClick={() => {
                  void navigator.clipboard.writeText(data.Link);
                }}
              >
                {t('copyUrl', { defaultValue: 'Copy URL' })}
              </Button>
            )}
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.ELinxHistoryItem } => ({
      id: String(item.ELinxRequestID || ''),
      data: item,
    })),
  };

  const exportColumns = tableProps.columns
    .filter((col) => col.id !== 'view')
    .map((col) => {
      if (col.id === 'requestStatus') {
        return {
          ...col,
          // Explicitly unset so the export column satisfies the template-based
          // (CustomColConfig) side of the column union.
          field: undefined,
          template: ({ data }: { data: vp.ELinxHistoryItem }) => (
            <Box flx="row" ai="center">
              <Badge label={capitalizeFirstLetter(data.Status)} color={GetColorStatus(data.Status)} />
            </Box>
          ),
        };
      }

      return col;
    });

  const tableExportProps: vp.TableProps<vp.ELinxHistoryItem> = {
    ...tableProps,
    id: 'export-elinx-history-table',
    columns: exportColumns,
    rows: exportList.map((item): { id: string; data: vp.ELinxHistoryItem } => ({
      id: String(item.ELinxRequestID || ''),
      data: item,
    })),
  };

  return (
    <Box flx="col" gap="md">
      <Box ai="center" jc="sb">
        <ActivityType
          activityTypeOptions={activityTypeOptions}
          currentType={currentType}
          handleCurrentType={handleCurrentType}
        />

        <ElinxHistoryExport onExport={handleExport} />
      </Box>

      <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
        <CardTabs.List aria-label={t('tabsLabel', { defaultValue: 'Elinx History' })}>
          <CardTabs.Trigger value="account">
            <span>{t('Tabs.account', { defaultValue: 'Connection Requests' })}</span>
          </CardTabs.Trigger>

          <CardTabs.Trigger value="payment">
            <span>{t('Tabs.payment', { defaultValue: 'Transaction Requests' })}</span>
          </CardTabs.Trigger>
        </CardTabs.List>

        {['account', 'payment'].map((tab) => (
          <CardTabs.Content key={tab} value={tab}>
            <Box flx="col" gap="sm">
              <TableFiltersContainer filtersApplied={filtersApplied}>
                <Box ai="center" jc="fe">
                  <TableFiltersContainer.Trigger />
                </Box>

                <TableFiltersContainer.Content>
                  <ElinxHistorySearchForm
                    requestStatus={filters.status}
                    timeZone={filters.timeZone}
                    searchTerm={filters.searchTerm}
                    hasInvalidEndDate={hasInvalidEndDate}
                    shouldShowEndDateError={shouldShowEndDateError}
                    setShouldShowEndDateError={setShouldShowEndDateError}
                    initialTimeRange={[toDayTimestamp(filters.startDate), toDayTimestamp(filters.endDate)]}
                    search={(searchParams) => {
                      const next: Filters = {
                        status: searchParams.requestStatus,
                        searchTerm: searchParams.searchTerm,
                        startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                        endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                        timeZone: searchParams.timeZone,
                      };

                      if (persistFiltersInUrl) {
                        updateQueryParams({
                          status: next.status,
                          searchTerm: next.searchTerm,
                          startDate: next.startDate,
                          endDate: next.endDate,
                          timeZone: next.timeZone,
                        });
                      }

                      setFilters(next);

                      fetchElinxHistory({
                        ...next,
                        hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
                        offset: setPageIndex(0),
                      });
                    }}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>

            <div style={{ display: 'none' }}>
              <Table {...tableExportProps} />
            </div>

            <Box>{fetching ? <Skeleton height="200px" /> : <Table {...tableProps} />}</Box>

            {!fetching && tableProps.rows && tableProps.rows.length === 0 && (
              <Box flx="col" jc="center">
                <Box ai="center" jc="center" pt="md">
                  {t('noRecordMessage', { defaultValue: 'There are no records to display within this date range.' })}
                </Box>
              </Box>
            )}

            <Box width="100%" jc="center" mt="lg" mb="sm">
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  forcePageNumber={pageIndex}
                  handlePageClick={(e) => {
                    const offset = setPageIndex(e.selected);

                    fetchElinxHistory({
                      ...filters,
                      hasInvalidEndDate: filters.startDate > filters.endDate,
                      offset,
                    });
                  }}
                />
              )}
            </Box>
          </CardTabs.Content>
        ))}
      </CardTabs.Root>
    </Box>
  );
};

export default ElinxHistoryTable;
